import cloneDeep from "lodash-es/cloneDeep";
import debounce from "lodash-es/debounce";
import merge from "lodash-es/merge";
import isEqual from "lodash-es/isEqual";
import type { PaginationMeta } from "./request";
import type { Pagination } from "~/types";

interface Emits {
  (e: "update:pagination", pagination: Pagination): void;
}

export function usePagination(paginationData?: Partial<Pagination>) {
  const pagination = ref<Pagination>(
    merge(paginationData ?? {}, {
      itemsPerPage: 10,
      page: 1,
      totalPages: 1,
      total: 0,
    }),
  );

  const paginationQueryParams = computed(() => {
    return {
      limit: pagination.value.itemsPerPage,
      page: pagination.value.page,
    };
  });

  function resetPagination() {
    pagination.value = {
      itemsPerPage: 10,
      page: 1,
      totalPages: 1,
      total: 0,
    };
  }

  function updateTableParams({
    page,
    itemsPerPage,
  }: {
    page: number;
    itemsPerPage: number;
    sortBy: string;
  }) {
    pagination.value = {
      ...pagination.value,
      page,
      itemsPerPage: itemsPerPage > 0 ? itemsPerPage : pagination.value.total,
    };
  }

  function updatePaginationOnScroll(scrollListLength: number) {
    if (pagination.value.total && pagination.value.total > scrollListLength) {
      pagination.value.itemsPerPage += 10;
      pagination.value = cloneDeep(pagination.value);
    }
  }

  return {
    pagination,
    paginationQueryParams,
    isDefaultPaginationValue,
    paginationSync,
    convertTablePaginationProps,
    convertPaginationToParams,
    formatPaginationmeta,
    updateTableParams,
    resetPagination,
    updatePaginationOnScroll: debounce(updatePaginationOnScroll, 1000),
  };
}

export function paginationSync(pagination: Ref<Pagination>, emits?: Emits) {
  const localPagination = computed({
    set(pagination: Pagination) {
      if (emits) emits("update:pagination", pagination);
      else {
        // do nothing
      }
    },
    get() {
      return pagination.value;
    },
  });

  return {
    localPagination,
  };
}

export function convertTablePaginationProps(
  tableParams: {
    take?: number;
    skip?: number;
  },
  pagination: Pagination,
): Partial<Pagination> {
  const { take } = tableParams;

  const { itemsPerPage, page } = pagination;
  return {
    page,
    itemsPerPage: take ?? itemsPerPage,
  };
}

export function convertPaginationToParams(
  pagination: Pick<Pagination, "itemsPerPage" | "page">,
): Partial<PaginationParams> {
  return {
    page: pagination.page,
    limit: pagination.itemsPerPage,
  };
}

export function formatPaginationmeta(
  paginationMeta?: PaginationMeta,
): Partial<Pagination> {
  if (!paginationMeta) return {};
  return {
    itemsPerPage: paginationMeta.perPage,
    page: paginationMeta.currentPage,
    totalPages: paginationMeta.totalPages,
    total: paginationMeta.total,
  };
}

export function isDefaultPaginationValue(pagination: Pagination) {
  return isEqual(pagination, {
    itemsPerPage: 10,
    page: 1,
    totalPages: 1,
    total: 0,
  });
}
